import React, { useState, useEffect } from 'react';
import { 
  Clock, MessageSquare, Calendar, TrendingUp, 
  Target, Award, AlertCircle, CheckCircle2,
  ArrowUpRight, ArrowDownRight, Zap, Users,
  Phone, Activity, BarChart3
} from 'lucide-react';
import { 
  getMyDirectMetrics, getMyAchievements, getUnreadAlerts 
} from '../services/attendanceApi';

function AttendeeDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [achievements, setAchievements] = useState({ achievements: [], totalPoints: 0 });
  const [unreadAlerts, setUnreadAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      setCurrentUser(payload);

      const [metricsRes, achievementsRes, alertsRes] = await Promise.all([
        getMyDirectMetrics(),
        getMyAchievements(),
        getUnreadAlerts()
      ]);

      console.log('📊 Métricas recebidas:', metricsRes.data);
      
      setMetrics(metricsRes.data);
      setAchievements(achievementsRes.data);
      setUnreadAlerts(alertsRes.data);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return '—';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  const today = metrics?.today || {};
  const week = metrics?.week || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">
            Olá, {currentUser?.name || 'Atendente'}! 👋
          </h1>
          <p className="text-gray-500 dark:text-slate-400">Acompanhe seu desempenho de atendimento</p>
        </div>
        {unreadAlerts.length > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl">
            <AlertCircle size={18} className="text-orange-600" />
            <span className="text-sm font-medium text-orange-700 dark:text-orange-400">
              {unreadAlerts.length} alerta{unreadAlerts.length > 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>

      {/* Alertas não lidos */}
      {unreadAlerts.length > 0 && (
        <div className="bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 border border-orange-200 dark:border-orange-800 rounded-2xl p-4">
          <h3 className="text-sm font-bold text-orange-900 dark:text-orange-300 mb-2 flex items-center gap-2">
            <AlertCircle size={16} />
            Alertas Recentes
          </h3>
          <div className="space-y-2">
            {unreadAlerts.slice(0, 3).map(alert => (
              <div key={alert.id} className="text-sm text-orange-800 dark:text-orange-300 bg-white/50 dark:bg-slate-800/50 rounded-lg p-2">
                {alert.message}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Status Atual */}
      <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              <Activity size={32} />
            </div>
            <div>
              <h2 className="text-xl font-bold">Status Atual</h2>
              <p className="opacity-90 text-sm">Conversas em andamento agora</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-5xl font-bold">{metrics?.activeNow || 0}</p>
            <p className="text-sm opacity-90">conversas ativas</p>
          </div>
        </div>
      </div>

      {/* Cards Principais - Hoje */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-xl">
              <MessageSquare size={22} />
            </div>
            <span className="text-xs font-medium text-green-600 dark:text-green-400">Hoje</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-slate-100">
            {today.total_conversations || 0}
          </p>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">Conversas atendidas</p>
        </div>

        <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2.5 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-xl">
              <Calendar size={22} />
            </div>
            <span className="text-xs font-medium text-green-600 dark:text-green-400">Hoje</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-slate-100">
            {metrics?.appointmentsToday || 0}
          </p>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">Agendamentos</p>
        </div>

        <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2.5 bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-xl">
              <Clock size={22} />
            </div>
            <span className="text-xs font-medium text-gray-500 dark:text-slate-400">Média</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-slate-100">
            {formatTime(metrics?.avgResponseSeconds)}
          </p>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">Tempo de resposta</p>
        </div>

        <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2.5 bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-xl">
              <Target size={22} />
            </div>
            <span className="text-xs font-medium text-gray-500 dark:text-slate-400">Taxa</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-slate-100">
            {metrics?.conversionRate || 0}%
          </p>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">Taxa de conversão</p>
        </div>
      </div>

      {/* Detalhamento de Hoje */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100 dark:border-slate-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
            <BarChart3 size={20} className="text-blue-600 dark:text-blue-400" />
            Resumo de Hoje
          </h2>
        </div>
        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-xl">
            <p className="text-3xl font-bold text-green-600 dark:text-green-400">
              {today.open_conversations || 0}
            </p>
            <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">Abertas</p>
          </div>
          <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl">
            <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">
              {today.pending_conversations || 0}
            </p>
            <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">Pendentes</p>
          </div>
          <div className="text-center p-4 bg-gray-50 dark:bg-slate-700 rounded-xl">
            <p className="text-3xl font-bold text-gray-600 dark:text-slate-300">
              {today.closed_conversations || 0}
            </p>
            <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">Fechadas</p>
          </div>
          <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
            <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
              {today.unique_contacts || 0}
            </p>
            <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">Contatos únicos</p>
          </div>
        </div>
      </div>

      {/* Banner de Pontos */}
      <div className="bg-gradient-to-r from-yellow-500 to-orange-600 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              <Award size={32} />
            </div>
            <div>
              <h2 className="text-xl font-bold">Suas Conquistas</h2>
              <p className="opacity-90 text-sm">
                {achievements.achievements?.length || 0} medalhas conquistadas
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-4xl font-bold">{achievements.totalPoints || 0}</p>
            <p className="text-sm opacity-90">pontos totais</p>
          </div>
        </div>
      </div>

      {/* Últimas Conquistas */}
      {achievements.achievements && achievements.achievements.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
              <Award size={20} className="text-yellow-500" />
              Conquistas Recentes
            </h2>
            <a href="/achievements" className="text-sm text-green-600 dark:text-green-400 hover:text-green-700 font-medium">
              Ver todas →
            </a>
          </div>
          <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
            {achievements.achievements.slice(0, 4).map((ach, idx) => (
              <div key={idx} className="text-center p-3 bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-xl border border-yellow-100 dark:border-yellow-800">
                <div className="text-4xl mb-2">{ach.icon}</div>
                <p className="text-xs font-bold text-gray-900 dark:text-slate-100">{ach.name}</p>
                <p className="text-xs text-orange-600 dark:text-orange-400 font-semibold mt-1">
                  +{ach.points_earned} pts
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Estatísticas dos Últimos 7 Dias */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100 dark:border-slate-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
            <TrendingUp size={20} className="text-green-600 dark:text-green-400" />
            Desempenho dos Últimos 7 Dias
          </h2>
        </div>
        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">Total de Conversas</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">
              {week.total_conversations || 0}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">Conversas Fechadas</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">
              {week.closed_conversations || 0}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">Tempo Médio</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">
              {formatTime(week.avg_duration_seconds)}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">Contatos Únicos</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">
              {week.unique_contacts || 0}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AttendeeDashboard;